// ── 권한 배정 커스텀 훅 ──────────────────────────────────────────────────────
// PermissionAssignment 컴포넌트의 모든 상태·효과·메모·핸들러를 담당한다.
// isDelegate=false → 관리자 전용, isDelegate=true → WORK_ASSIGNMENT 보유자 전용
import { useEffect, useMemo, useState } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';

// ── 헬퍼 함수 ─────────────────────────────────────────────────────────────────
// 기능셋 표시명 정규화 (DB 이름 그대로 반환)
export function displayFeatureSetName(rawName) {
  return String(rawName || '').trim();
}

// 검색 키워드 정규화 (소문자 + 앞뒤 공백 제거)
function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

// isDelegate에 따른 API 경로 prefix
function apiPrefix(isDelegate) {
  return isDelegate ? '/api/sub' : '/api/admin';
}

// isDelegate에 따른 테마 색상 (관리자=파랑, 위임자=청록)
function themeColor(isDelegate) {
  return isDelegate ? '#0369a1' : '#1d4ed8';
}

/**
 * 권한 배정 훅
 * @param {{ isDelegate: boolean, onPermissionsChanged: function|undefined }} opts
 * @returns 상태, 파생값, 핸들러 전체
 */
export function usePermissionAssignment({ isDelegate, onPermissionsChanged }) {
  const { showToast } = useToast();

  // ── 반응형 상태 ─────────────────────────────────────────────────────────
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);

  // ── 배정 유형 상태 ───────────────────────────────────────────────────────
  const [assignKind,    setAssignKind]    = useState('task'); // 'task' | 'feature'
  const [taskCode,      setTaskCode]      = useState('');
  const [featureSetId,  setFeatureSetId]  = useState(null);

  // ── 서버 데이터 상태 ─────────────────────────────────────────────────────
  const [taskOptions,   setTaskOptions]   = useState([]);
  const [featureSets,   setFeatureSets]   = useState([]);
  const [employees,     setEmployees]     = useState([]);
  const [assigned,      setAssigned]      = useState([]);
  const [loading,       setLoading]       = useState(false);

  // ── 검색 필터 상태 ───────────────────────────────────────────────────────
  const [keyword,       setKeyword]       = useState('');
  const [savingUserId,  setSavingUserId]  = useState(null);

  // ── API 설정 ─────────────────────────────────────────────────────────────
  const prefix = apiPrefix(isDelegate);
  const color  = themeColor(isDelegate);

  // ── 파생값: 배정된 직원 ID 집합 (빠른 lookup용) ──────────────────────────
  const assignedIdSet = useMemo(
    () => new Set((assigned || []).map((row) => Number(row.user_id))),
    [assigned]
  );

  // ── 파생값: 키워드 필터 적용 직원 목록 ────────────────────────────────────
  const filteredEmployees = useMemo(() => {
    const q = normalizeText(keyword);
    if (!q) return employees;
    return employees.filter((emp) => {
      const name    = normalizeText(emp.name);
      const loginId = normalizeText(emp.user_id);
      const dept    = normalizeText(emp.department);
      return name.includes(q) || loginId.includes(q) || dept.includes(q);
    });
  }, [employees, keyword]);

  // ── 파생값: 배정 / 미배정 직원 분리 ───────────────────────────────────────
  const assignedEmployees   = useMemo(
    () => filteredEmployees.filter((emp) =>  assignedIdSet.has(Number(emp.id))),
    [filteredEmployees, assignedIdSet]
  );
  const unassignedEmployees = useMemo(
    () => filteredEmployees.filter((emp) => !assignedIdSet.has(Number(emp.id))),
    [filteredEmployees, assignedIdSet]
  );

  // ── 파생값: 현재 선택된 기능셋 객체 ──────────────────────────────────────
  const selectedFeatureSet = useMemo(
    () => featureSets.find((fs) => Number(fs.id) === Number(featureSetId)) || null,
    [featureSets, featureSetId]
  );

  // ── 서버에서 배정 데이터 로드 ─────────────────────────────────────────────
  async function fetchData(next = {}) {
    const nextKind         = next.kind         || assignKind;
    const nextTaskCode     = next.taskCode     || taskCode;
    const nextFeatureSetId = Number(next.featureSetId || featureSetId || 0) || null;

    const params = new URLSearchParams();
    params.set('kind', nextKind);
    if (nextKind === 'task'    && nextTaskCode)     params.set('taskCode',      nextTaskCode);
    if (nextKind === 'feature' && nextFeatureSetId) params.set('featureSetId',  String(nextFeatureSetId));

    setLoading(true);
    const result = await apiClient.get(`${prefix}/work-feature-assignments?${params.toString()}`);
    setLoading(false);

    if (!result.ok) {
      showToast(result.data?.message || '배정 데이터를 불러오지 못했습니다.', 'error');
      return;
    }

    const serverTaskOptions = Array.isArray(result.data?.taskOptions) ? result.data.taskOptions : [];
    const serverFeatureSets = Array.isArray(result.data?.featureSets) ? result.data.featureSets : [];
    const serverKind        = String(result.data?.kind || nextKind);

    setTaskOptions(serverTaskOptions);
    setFeatureSets(serverFeatureSets);
    setEmployees(Array.isArray(result.data?.employees) ? result.data.employees : []);
    setAssigned(Array.isArray(result.data?.assigned)   ? result.data.assigned  : []);

    // 서버 응답의 선택값으로 로컬 상태 동기화
    if (serverKind === 'task') {
      const serverCode = String(result.data?.taskCode || serverTaskOptions[0]?.code || '');
      if (serverCode !== taskCode) setTaskCode(serverCode);
    } else {
      const fallbackId  = serverFeatureSets[0]?.id || null;
      const serverFsId  = Number(result.data?.featureSetId || fallbackId || 0) || null;
      if (serverFsId !== featureSetId) setFeatureSetId(serverFsId);
    }
  }

  // ── 권한 배정/회수 공통 ──────────────────────────────────────────────────
  async function mutateAssignment(action, spec) {
    const nextKind = spec?.kind || assignKind;
    const nextTaskCode = nextKind === 'task' ? (spec?.taskCode || taskCode) : undefined;
    const nextFeatureSetId = nextKind === 'feature' ? (spec?.featureSetId || featureSetId) : undefined;
    const userId = spec?.userId;

    setSavingUserId(userId);
    try {
      const result = await apiClient.post(`${prefix}/work-feature-assignments/${action}`, {
        kind: nextKind,
        taskCode: nextTaskCode,
        featureSetId: nextFeatureSetId,
        userId,
      });

      if (!result.ok) {
        const fallback = action === 'grant' ? '배정에 실패했습니다.' : '배정 해제에 실패했습니다.';
        showToast(result.data?.message || fallback, 'error');
        return false;
      }

      const kindLabel = nextKind === 'task' ? '업무' : '기능';
      showToast(action === 'grant' ? `${kindLabel}를 배정했습니다.` : `${kindLabel} 배정을 해제했습니다.`, 'success');
      await fetchData({ kind: nextKind, taskCode: nextTaskCode, featureSetId: nextFeatureSetId });
      // 배정된 사용자가 현재 로그인한 경우 탭 즉시 반영
      if (onPermissionsChanged) onPermissionsChanged();
      return true;
    } finally {
      setSavingUserId(null);
    }
  }

  // ── 현재 선택된 업무/기능 배정 ───────────────────────────────────────────
  async function grantTask(userId) {
    return mutateAssignment('grant', { kind: assignKind, taskCode, featureSetId, userId });
  }

  async function revokeTask(userId) {
    return mutateAssignment('revoke', { kind: assignKind, taskCode, featureSetId, userId });
  }

  // ── 화면 크기 변화 감지 ──────────────────────────────────────────────────
  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth <= 900); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 업무 배정 모드: assignKind 또는 taskCode 변경 시 재조회
  useEffect(() => {
    if (assignKind === 'task') fetchData({ kind: 'task', taskCode });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignKind, taskCode]);

  // 기능 배정 모드: assignKind 또는 featureSetId 변경 시 재조회
  useEffect(() => {
    if (assignKind === 'feature') fetchData({ kind: 'feature', featureSetId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignKind, featureSetId]);

  return {
    // UI 상태
    isMobile,
    // 배정 유형
    assignKind, setAssignKind,
    taskCode,   setTaskCode,
    featureSetId, setFeatureSetId,
    // 서버 데이터
    taskOptions, featureSets, employees, assigned, loading,
    // 검색
    keyword, setKeyword, savingUserId,
    // 파생값
    assignedIdSet, filteredEmployees, assignedEmployees, unassignedEmployees, selectedFeatureSet,
    // 테마
    prefix, color,
    // 핸들러
    fetchData, grantTask, revokeTask,
    grantAssignment: (spec) => mutateAssignment('grant', spec),
    revokeAssignment: (spec) => mutateAssignment('revoke', spec),
  };
}
